import {
  CalendarBlank,
  Link,
  Package,
  PencilSimple,
  ShoppingCart,
  Truck,
  TShirt,
  Warning,
} from "@phosphor-icons/react"
import { toast } from "react-toastify"

import { useSaveMetadata } from "~/hooks/useMetadata"
import { type IVariantSummary } from "~/hooks/useVariantTableData"
import { api } from "~/utils/api"
import {
  DateFormatType,
  formatDate,
  getSoldAndActiveSince,
  startOfTodayUTC,
} from "~/utils/datetime"
import { getInfoMatch, getInfoTooltip } from "~/utils/forecast-alert-utils"
import { formatNumber } from "~/utils/formatting"
import { formatName } from "~/utils/namingUtils"
import { Badge } from "~/components/reusable/Badge"
import { Button } from "~/components/reusable/Button"
import {
  BreakdownHoverCard,
  BreakdownHoverCardHeader,
} from "~/components/reusable/hovercard/BreakdownHoverCard"
import { HoverCardFooter } from "~/components/reusable/hovercard/HoverCard"
import HoverCardRow from "~/components/reusable/hovercard/HoverCardRow"
import HoverCardRowWrapper from "~/components/reusable/hovercard/HoverCardRowWrapper"
import { Tooltip } from "~/components/reusable/Tooltip"

type InfoId = NonNullable<ReturnType<typeof getInfoMatch>>["id"]
type CardRow = { icon: React.ReactNode; label: string; value: string }

export function DemandInfoBadge({
  data,
  label,
  trigger,
}: {
  data: IVariantSummary | undefined
  label: string
  /** Replaces the default labelled badge, for callers that need a narrow cell. */
  trigger?: React.ReactNode
}) {
  const infoId = getInfoMatch(data)?.id

  const badge = trigger ?? (
    <div>
      <Badge size="xs" variant="neutral">
        <Warning format="outline" weight="bold" className="mr-1 inline" size={12} />
        {label}
      </Badge>
    </div>
  )

  if (!data || !infoId) {
    return <Tooltip title={getInfoTooltip(data)}>{badge}</Tooltip>
  }

  if (infoId === "phasedOut" || infoId === "phasingOut") {
    return (
      <PhaseOutHoverCard data={data} label={label} phasedOut={infoId === "phasedOut"}>
        {badge}
      </PhaseOutHoverCard>
    )
  }

  const { description, rows } = getInfoCardContent(infoId, data)
  return (
    <InfoCardShell label={label} description={description} rows={rows}>
      {badge}
    </InfoCardShell>
  )
}

const fmtDate = (d: Date | null | undefined) =>
  d ? formatDate(d, DateFormatType.MonthYear) : "Unknown"
const fmtWeeks = (w: number) => `${w} week${w === 1 ? "" : "s"}`

function getInfoCardContent(
  infoId: Exclude<InfoId, "phasedOut" | "phasingOut">,
  data: IVariantSummary,
): { description: string; rows: CardRow[] } {
  switch (infoId) {
    case "fashionCollection":
      return {
        description: "Demand is based on a fashion collection model.",
        rows: [
          {
            icon: <TShirt size={14} />,
            label: "Fashion demand",
            value: fmtWeeks(data.forecastAlertAggregate?.fashionWeeks ?? 0),
          },
        ],
      }
    case "manuallyMatched": {
      const count = data.forecastAlertAggregate?.customMatchCount ?? 0
      return {
        description:
          count > 0
            ? "Demand is based on manually selected matches."
            : "Demand is based on a manual match.",
        rows:
          count > 0
            ? [{ icon: <Link size={14} />, label: "Manual matches", value: String(count) }]
            : [],
      }
    }
    case "adjusted":
      return {
        description: "Demand has been manually adjusted.",
        rows: [
          {
            icon: <PencilSimple size={14} />,
            label: "Adjusted demand",
            value: fmtWeeks(data.forecastAlertAggregate?.adjustedWeeks ?? 0),
          },
        ],
      }
    case "shortSaleHorizonReactivated": {
      const noosStartDate = (data.settings as { noosStartDate?: Date })?.noosStartDate
      const activeSince = getSoldAndActiveSince(data.soldSince, noosStartDate)
      return {
        description: `Less than 52 weeks of ${formatName("sale", true)} data.`,
        rows: [
          {
            icon: <CalendarBlank size={14} />,
            label: "Active & selling since",
            value: fmtDate(activeSince),
          },
          {
            icon: <ShoppingCart size={14} />,
            label: `First ${formatName("sale")}`,
            value: fmtDate(data.soldSince),
          },
        ],
      }
    }
    case "shortSaleHorizon":
      return {
        description: `Less than 52 weeks of ${formatName("sale", true)} data.`,
        rows: [
          { icon: <ShoppingCart size={14} />, label: "Sold since", value: fmtDate(data.soldSince) },
        ],
      }
    case "shortDemandHorizon": {
      const activatedUntil = (data.settings as { activatedUntil?: Date })?.activatedUntil
      return {
        description: "Product deactivates within 52 weeks.",
        rows: [
          {
            icon: <CalendarBlank size={14} />,
            label: "Deactivates",
            value: fmtDate(activatedUntil),
          },
        ],
      }
    }
  }
}

function InfoCardShell({
  label,
  description,
  rows,
  footer,
  children,
}: {
  label: string
  description: string
  rows: CardRow[]
  footer?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <BreakdownHoverCard trigger={children} className="w-90">
      <BreakdownHoverCardHeader title={label} description={description} />
      {rows.length > 0 && (
        <HoverCardRowWrapper>
          {rows.map((row) => (
            <HoverCardRow key={row.label} icon={row.icon} label={row.label} value={row.value} />
          ))}
        </HoverCardRowWrapper>
      )}
      {footer}
    </BreakdownHoverCard>
  )
}

function PhaseOutHoverCard({
  data,
  label,
  phasedOut,
  children,
}: {
  data: IVariantSummary
  label: string
  phasedOut: boolean
  children: React.ReactNode
}) {
  const saveMetadata = useSaveMetadata({ showToast: false })
  const { data: company } = api.company.getCompany.useQuery()
  const phasingOutEnabled = company?.phasingOutSellDownForecast ?? false

  const phasingOutFrom = data.settings?.phasingOutFrom

  const description = phasedOut
    ? "Product is phased out. No demand is generated."
    : phasingOutEnabled
      ? "Product is being phased out. Demand is limited to selling down remaining stock."
      : "Product is being phased out. Demand tapers off from the phase-out date."

  const phaseOutNow = () => {
    if (data.skuIds.length === 0) return
    saveMetadata.mutate(
      {
        skuIds: data.skuIds,
        lifecycleStatus: "phasedOut",
        phasingOutFrom: null,
        activatedUntil: startOfTodayUTC(),
      },
      { onSuccess: () => toast.success("Variant phased out") },
    )
  }

  const rows: CardRow[] = [
    ...(!phasedOut && phasingOutFrom
      ? [
          {
            icon: <CalendarBlank size={14} />,
            label: "Phasing out from",
            value: formatDate(phasingOutFrom, DateFormatType.MonthYear),
          },
        ]
      : []),
    { icon: <Package size={14} />, label: "Stock level", value: formatNumber(data.stockLevel) },
    ...(data.incomingStock > 0
      ? [
          {
            icon: <Truck size={14} />,
            label: "Incoming stock",
            value: formatNumber(data.incomingStock),
          },
        ]
      : []),
  ]

  return (
    <InfoCardShell
      label={label}
      description={description}
      rows={rows}
      footer={
        !phasedOut && (
          <HoverCardFooter className="flex justify-end bg-subtle py-2.5">
            <Button
              size="xs"
              variant={data.stockLevel === 0 && data.incomingStock <= 0 ? "primary" : "neutral"}
              requires="edit"
              onClick={phaseOutNow}
              disabled={saveMetadata.isPending || data.skuIds.length === 0}
            >
              {saveMetadata.isPending ? "Phasing out..." : "Mark as phased out"}
            </Button>
          </HoverCardFooter>
        )
      }
    >
      {children}
    </InfoCardShell>
  )
}
